/**
 * Shared live activity log for operational recovery.
 * Header, Engineering remediation, and Executions subscribe to the same store.
 *
 * Important: skill-exec mirroring must batch writes and key off a stable fingerprint,
 * otherwise multi-mount + poll refetch storms can exceed React's update depth
 * (symptoms: useContext null / operate-outlet crash).
 */
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import type { SkillExecutionRecord } from '../services/api';

export type OpsActivitySource =
  | 'analyze'
  | 'apply'
  | 'decision'
  | 'skill-exec'
  | 'rerun'
  | 'system'
  | 'attestation';

export interface OpsActivityLine {
  id: string;
  at: string;
  text: string;
  source: OpsActivitySource;
}

const EVENT = 'ensomirai:ops-activity';
const MAX_LINES = 40;

const storageKey = (projectId: string, incidentId: string) =>
  `ensomirai.ops.activity.v1.${projectId}.${incidentId}`;

const snapshotCache = new Map<string, { raw: string | null; value: OpsActivityLine[] }>();

/** Module-level seen map so Header/Studio/Executions don't re-emit the same skill rows. */
const skillSeenByIncident = new Map<string, Map<string, string>>();

function notify(projectId: string, incidentId: string): void {
  try {
    window.dispatchEvent(
      new CustomEvent(EVENT, { detail: { projectId, incidentId } }),
    );
  } catch {
    /* ignore */
  }
}

const EMPTY: OpsActivityLine[] = [];

function readSnapshot(projectId: string, incidentId: string): OpsActivityLine[] {
  if (!projectId || !incidentId) return EMPTY;
  const k = storageKey(projectId, incidentId);
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(k);
  } catch {
    raw = null;
  }
  const cached = snapshotCache.get(k);
  if (cached && cached.raw === raw) return cached.value;

  let value: OpsActivityLine[] = EMPTY;
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as OpsActivityLine[];
      if (Array.isArray(parsed) && parsed.length) value = parsed;
    } catch {
      value = EMPTY;
    }
  }
  snapshotCache.set(k, { raw, value });
  return value;
}

function writeLines(projectId: string, incidentId: string, lines: OpsActivityLine[]): void {
  const k = storageKey(projectId, incidentId);
  const trimmed = lines.slice(-MAX_LINES);
  const raw = JSON.stringify(trimmed);
  try {
    sessionStorage.setItem(k, raw);
  } catch {
    /* ignore */
  }
  snapshotCache.set(k, { raw, value: trimmed });
  notify(projectId, incidentId);
}

export function loadOpsActivityLog(projectId: string, incidentId: string): OpsActivityLine[] {
  return readSnapshot(projectId, incidentId);
}

export function clearOpsActivityLog(projectId: string, incidentId: string): void {
  const k = storageKey(projectId, incidentId);
  try {
    sessionStorage.removeItem(k);
  } catch {
    /* ignore */
  }
  snapshotCache.set(k, { raw: null, value: EMPTY });
  skillSeenByIncident.delete(`${projectId}:${incidentId}`);
  notify(projectId, incidentId);
}

export function appendOpsActivity(
  projectId: string,
  incidentId: string,
  text: string,
  source: OpsActivitySource = 'system',
): OpsActivityLine | null {
  const trimmed = String(text || '').trim();
  if (!projectId || !incidentId || !trimmed) return null;
  const prev = readSnapshot(projectId, incidentId);
  const last = prev[prev.length - 1];
  if (last && last.text === trimmed && last.source === source) {
    const age = Date.now() - Date.parse(last.at);
    if (Number.isFinite(age) && age < 2000) return last;
  }
  const line: OpsActivityLine = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    at: new Date().toISOString(),
    text: trimmed.slice(0, 320),
    source,
  };
  writeLines(projectId, incidentId, [...prev, line]);
  return line;
}

/** Append many lines with a single store notify (avoids update storms). */
export function appendOpsActivityBatch(
  projectId: string,
  incidentId: string,
  entries: Array<{ text: string; source?: OpsActivitySource }>,
): void {
  if (!projectId || !incidentId || !entries.length) return;
  let lines = readSnapshot(projectId, incidentId);
  let changed = false;
  const now = Date.now();
  for (const entry of entries) {
    const trimmed = String(entry.text || '').trim().slice(0, 320);
    if (!trimmed) continue;
    const source = entry.source || 'system';
    const last = lines[lines.length - 1];
    if (last && last.text === trimmed && last.source === source) {
      const age = now - Date.parse(last.at);
      if (Number.isFinite(age) && age < 2000) continue;
    }
    lines = [
      ...lines,
      {
        id: `${now}-${Math.random().toString(36).slice(2, 8)}`,
        at: new Date(now).toISOString(),
        text: trimmed,
        source,
      },
    ];
    changed = true;
  }
  if (changed) writeLines(projectId, incidentId, lines);
}

/** Subscribe to the activity log for one incident. */
export function useOpsActivityLog(
  projectId: string,
  incidentId: string | null | undefined,
): OpsActivityLine[] {
  const id = incidentId || '';
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const handler = (e: Event) => {
        const detail = (e as CustomEvent<{ projectId?: string; incidentId?: string }>).detail;
        if (detail && detail.projectId === projectId && detail.incidentId === id) {
          onStoreChange();
        }
      };
      window.addEventListener(EVENT, handler);
      return () => window.removeEventListener(EVENT, handler);
    },
    [projectId, id],
  );
  const getSnapshot = useCallback(() => readSnapshot(projectId, id), [projectId, id]);
  return useSyncExternalStore(subscribe, getSnapshot, () => EMPTY);
}

function executionsFingerprint(executions: SkillExecutionRecord[] | undefined): string {
  if (!executions?.length) return '';
  return executions
    .slice(0, 20)
    .map((ex) => `${ex.id}|${ex.status}|${ex.phase}|${ex.summary || ''}`)
    .join(';;');
}

/**
 * Watch skill-execution polls and append activity lines when status/phase changes.
 * Prefer mounting this once (Mission header). Safe if mounted multiple times —
 * seen state is shared per incident.
 */
export function useOpsSkillExecActivityMirror(
  projectId: string,
  incidentId: string | null | undefined,
  executions: SkillExecutionRecord[] | undefined,
): void {
  const id = incidentId || '';
  const fingerprint = useMemo(() => executionsFingerprint(executions), [executions]);

  useEffect(() => {
    if (!projectId || !id || !fingerprint || !executions?.length) return;

    const seenKey = `${projectId}:${id}`;
    let seen = skillSeenByIncident.get(seenKey);
    if (!seen) {
      seen = new Map();
      skillSeenByIncident.set(seenKey, seen);
    }

    const relevant = executions
      .filter((ex) => {
        const hay = `${ex.skill_id || ''} ${ex.phase || ''} ${ex.summary || ''}`.toLowerCase();
        return /remediat|ops|sftp|inventory|sync|recover|operational|delivery|data.?repair/.test(
          hay,
        );
      })
      .slice(0, 12);

    const pool = relevant.length > 0 ? relevant : executions.slice(0, 6);
    const batch: Array<{ text: string; source: OpsActivitySource }> = [];

    for (const ex of pool) {
      const key = ex.id || `${ex.skill_id}-${ex.computed_at}`;
      const sig = `${ex.status}|${ex.phase}|${ex.summary || ''}`;
      const prev = seen.get(key);
      if (prev === sig) continue;
      const isNew = prev === undefined;
      seen.set(key, sig);
      const label = (ex.summary || ex.skill_id || 'skill').replace(/_/g, ' ');
      const phase = ex.phase ? ` · ${ex.phase.replace(/_/g, ' ')}` : '';
      batch.push({
        text: isNew
          ? `Skill-exec: ${label}${phase} → ${ex.status}`
          : `Skill-exec update: ${label}${phase} → ${ex.status}`,
        source: 'skill-exec',
      });
    }

    if (batch.length) appendOpsActivityBatch(projectId, id, batch);
  }, [projectId, id, fingerprint, executions]);
}
